import * as THREE from "three";
import type { Player, CharacterSkin } from "../player/player";
import {
  ARMOR_BOOTS,
  ARMOR_CHESTPLATE,
  ARMOR_HELMET,
  ARMOR_LEGGINGS,
} from "../player/inventory";
import {
  armorInfo,
  ARROW,
  BONE,
  BOW,
  isBlockItem,
  isSword,
  isTool,
  STICK,
  type ItemStack,
} from "../world/items";
import { BLOCKS, STONE, TORCH } from "../world/blocks";
import type { AtlasBundle } from "../world/atlas";
import { createBlockBufferGeometry } from "./block-geometry";
import {
  createItemBufferGeometry,
  createItemBufferGeometryFromGrid,
} from "./item-geometry";
import { getBowPullingPixelGrid } from "../ui/icons";
import {
  createSteveTextures,
  createAlexTextures,
  type CharacterSkinTextures,
} from "./character-skins";
import { ArmorSetRegistry } from "./armor-textures";

/**
 * 3D character renderer for Steve and Alex matching Minecraft visual standards.
 * Supports:
 * - 1st-person viewmodel with character-specific sleeves and hands.
 * - 3rd-person character model with textured head, torso, slim/standard limbs, and animations.
 * - Companion character (renders both Steve and Alex in the world).
 */

const ALEX_HAIR_LOCK = 0xde6e27;
const FLASH = new THREE.Color(0xff2222);

interface ArmorRigMeshes {
  readonly helmet: THREE.Mesh;
  readonly chestTorso: THREE.Mesh;
  readonly chestLeftSleeve: THREE.Mesh;
  readonly chestRightSleeve: THREE.Mesh;
  readonly leggingsPelvis: THREE.Mesh;
  readonly leggingsLeftLeg: THREE.Mesh;
  readonly leggingsRightLeg: THREE.Mesh;
  readonly bootsLeft: THREE.Mesh;
  readonly bootsRight: THREE.Mesh;
}

interface CharacterRig {
  readonly rootGroup: THREE.Group;
  readonly headPivot: THREE.Group;
  readonly torsoGroup: THREE.Group;
  readonly leftArmPivot: THREE.Group;
  readonly rightArmPivot: THREE.Group;
  readonly leftLegPivot: THREE.Group;
  readonly rightLegPivot: THREE.Group;
  readonly rightArmMesh: THREE.Mesh;
  readonly leftArmMesh: THREE.Mesh;
  readonly tpItemPivot: THREE.Group;
  readonly tpItemMesh: THREE.Mesh;
  readonly tpBlockGroup: THREE.Group;
  readonly tpBlockMesh: THREE.Mesh;
  readonly hairLockMesh?: THREE.Mesh;
  readonly armorMeshes: ArmorRigMeshes;
  readonly materials: THREE.MeshBasicMaterial[];
  skin: CharacterSkin;
}

export class PlayerRenderer {
  /** Scene group for player's 3rd-person model. */
  readonly playerThirdPersonGroup: THREE.Group;
  /** Scene group for in-world companion (renders the other character). */
  readonly companionGroup: THREE.Group;
  /** Camera group for 1st-person viewmodel arm. */
  readonly firstPersonGroup: THREE.Group;

  private readonly boxGeometry = new THREE.BoxGeometry(1, 1, 1);

  private readonly steveTextures: CharacterSkinTextures;
  private readonly alexTextures: CharacterSkinTextures;

  private readonly armorRegistry = new ArmorSetRegistry();
  private readonly armorMaterials: THREE.MeshBasicMaterial[];

  private readonly playerRig: CharacterRig;
  private readonly companionRig: CharacterRig;

  // Block & Item geometry cache and materials
  private readonly blockGeometries = new Map<number, THREE.BufferGeometry>();
  private readonly itemGeometries = new Map<number, THREE.BufferGeometry>();
  private readonly bowPullGeometries = new Map<number, THREE.BufferGeometry>();
  private readonly fpBlockMat: THREE.MeshBasicMaterial;
  private readonly tpBlockMat: THREE.MeshBasicMaterial;
  private readonly fpItemMat: THREE.MeshBasicMaterial;
  private readonly tpItemMat: THREE.MeshBasicMaterial;

  // First-person viewmodel parts
  private readonly fpArmMesh: THREE.Mesh;
  private readonly steveFpArmGeo: THREE.BoxGeometry;
  private readonly alexFpArmGeo: THREE.BoxGeometry;
  private readonly fpArmMats: THREE.MeshBasicMaterial[] = [];
  private readonly fpArmMatrix = new THREE.Matrix4();
  private readonly fpArmTrans = new THREE.Matrix4();
  private readonly fpArmRot = new THREE.Matrix4();
  private readonly fpArmAxis = new THREE.Vector3();
  private readonly fpArmPos = new THREE.Vector3();
  private readonly fpArmQuat = new THREE.Quaternion();
  private readonly fpArmScale = new THREE.Vector3();
  private readonly fpItemMatrix = new THREE.Matrix4();
  private readonly fpItemTrans = new THREE.Matrix4();
  private readonly fpItemRot = new THREE.Matrix4();
  private readonly fpItemScaleMat = new THREE.Matrix4();
  private readonly fpItemAxis = new THREE.Vector3();
  private readonly fpItemPos = new THREE.Vector3();
  private readonly fpItemQuat = new THREE.Quaternion();
  private readonly fpItemScale = new THREE.Vector3();
  private readonly tpItemMatrix = new THREE.Matrix4();
  private readonly tpItemTrans = new THREE.Matrix4();
  private readonly tpItemRot = new THREE.Matrix4();
  private readonly tpItemScaleMat = new THREE.Matrix4();
  private readonly tpItemAxis = new THREE.Vector3();
  private readonly tpItemPos = new THREE.Vector3();
  private readonly tpItemQuat = new THREE.Quaternion();
  private readonly tpItemScale = new THREE.Vector3();
  private readonly fpItemMesh: THREE.Mesh;
  private readonly fpBlockGroup: THREE.Group;
  private readonly fpBlockMesh: THREE.Mesh;
  private readonly fpMaterials: { mat: THREE.MeshBasicMaterial; baseColor: THREE.Color }[] = [];

  private walkPhase = 0;
  private swingProgress = 0;
  /** Whether view bobbing is enabled (configurable from Settings) */
  viewBobbing = true;

  // Companion position & tracking state
  private readonly companionPos = new THREE.Vector3();
  private companionYaw = 0;
  private companionWalkPhase = 0;
  private companionInitialized = false;

  constructor(scene: THREE.Object3D, camera: THREE.Camera, atlas?: AtlasBundle) {
    this.steveTextures = createSteveTextures();
    this.alexTextures = createAlexTextures();
    this.armorMaterials = this.armorRegistry.allMaterials();

    this.fpBlockMat = new THREE.MeshBasicMaterial({
      map: atlas?.texture,
      vertexColors: true,
      fog: false,
      depthTest: false,
      depthWrite: false,
      transparent: true,
    });

    this.tpBlockMat = new THREE.MeshBasicMaterial({
      map: atlas?.texture,
      vertexColors: true,
      fog: true,
    });

    // 3D Extruded Item Materials (MUST be initialized before buildRig so tpItemMesh receives tpItemMat!)
    this.fpItemMat = new THREE.MeshBasicMaterial({
      vertexColors: true,
      fog: false,
      depthTest: false,
      depthWrite: false,
      transparent: true,
    });
    this.tpItemMat = new THREE.MeshBasicMaterial({
      vertexColors: true,
      fog: true,
      side: THREE.DoubleSide,
    });

    this.playerThirdPersonGroup = new THREE.Group();
    this.playerThirdPersonGroup.name = "player-third-person";
    scene.add(this.playerThirdPersonGroup);

    this.companionGroup = new THREE.Group();
    this.companionGroup.name = "companion-character";
    scene.add(this.companionGroup);

    this.firstPersonGroup = new THREE.Group();
    this.firstPersonGroup.name = "player-first-person";
    camera.add(this.firstPersonGroup);

    this.playerRig = this.buildRig(this.playerThirdPersonGroup, "steve");
    this.companionRig = this.buildRig(this.companionGroup, "alex");

    // --- Build First-Person Viewmodel ---
    // Authentic Minecraft arm dimensions and coordinate alignment:
    // Scale: 1/16 meter per pixel = 0.0625
    // Steve: 4x12x4 box centered at (-0.0625, 0.25, 0)
    // Alex:  3x12x4 box centered at (-0.03125, 0.25, 0)
    const sc = 0.0625;
    this.steveFpArmGeo = new THREE.BoxGeometry(4 * sc, 12 * sc, 4 * sc);
    this.steveFpArmGeo.translate(-1 * sc, 4 * sc, 0);

    this.alexFpArmGeo = new THREE.BoxGeometry(3 * sc, 12 * sc, 4 * sc);
    this.alexFpArmGeo.translate(-0.5 * sc, 4 * sc, 0);

    // 6 face materials for BoxGeometry with Minecraft Java directional shading:
    // 0: +X, 1: -X (bright front-right facet), 2: +Y (hand cap), 3: -Y (shoulder base), 4: +Z, 5: -Z (shaded front-left facet)
    const createFpMat = (map: THREE.CanvasTexture, shade = 1.0): THREE.MeshBasicMaterial => {
      const m = new THREE.MeshBasicMaterial({
        map,
        fog: false,
        depthTest: false,
        depthWrite: false,
        transparent: true,
      });
      const c = new THREE.Color().setScalar(shade);
      this.fpMaterials.push({ mat: m, baseColor: c });
      this.fpArmMats.push(m);
      return m;
    };

    createFpMat(this.steveTextures.fpArm, 1.0);          // 0: +X
    createFpMat(this.steveTextures.fpArm, 1.0);          // 1: -X (bright front-right facet)
    createFpMat(this.steveTextures.armHandBottom, 0.92); // 2: +Y (hand cap)
    createFpMat(this.steveTextures.armHandBottom, 0.80); // 3: -Y (shoulder base)
    createFpMat(this.steveTextures.fpArm, 0.82);         // 4: +Z
    createFpMat(this.steveTextures.fpArm, 0.82);         // 5: -Z (shaded front-left facet)

    this.fpArmMesh = new THREE.Mesh(this.steveFpArmGeo, this.fpArmMats);
    this.fpArmMesh.renderOrder = 9999;
    this.firstPersonGroup.add(this.fpArmMesh);

    // Held Block Viewmodel
    this.fpBlockGroup = new THREE.Group();
    this.fpBlockGroup.name = "fp-held-block";
    this.fpBlockMesh = new THREE.Mesh(this.getBlockGeometry(STONE), this.fpBlockMat);
    this.fpBlockMesh.scale.set(0.18, 0.18, 0.18);
    this.fpBlockMesh.renderOrder = 9999;
    this.fpBlockGroup.add(this.fpBlockMesh);
    this.fpBlockGroup.visible = false;
    this.firstPersonGroup.add(this.fpBlockGroup);

    this.fpItemMesh = new THREE.Mesh(this.boxGeometry, this.fpItemMat);
    this.fpItemMesh.renderOrder = 9999;
    this.fpItemMesh.visible = false;
    this.firstPersonGroup.add(this.fpItemMesh);

    this.firstPersonGroup.position.set(0, 0, 0);
    this.firstPersonGroup.rotation.set(0, 0, 0);
    this.firstPersonGroup.renderOrder = 9999;
  }

  private computeMinecraftArmTransform(
    swingProgress: number,
    bobX: number,
    bobY: number,
    isAlex: boolean,
  ): void {
    this.fpArmMatrix.identity();

    const translate = (x: number, y: number, z: number) => {
      this.fpArmTrans.makeTranslation(x, y, z);
      this.fpArmMatrix.multiply(this.fpArmTrans);
    };

    const rotate = (deg: number, x: number, y: number, z: number) => {
      this.fpArmAxis.set(x, y, z).normalize();
      this.fpArmRot.makeRotationAxis(this.fpArmAxis, (deg * Math.PI) / 180);
      this.fpArmMatrix.multiply(this.fpArmRot);
    };

    // 1. Procedural swing translation
    const var4 = -0.3 * Math.sin(Math.sqrt(swingProgress) * Math.PI);
    const var5 = 0.4 * Math.sin(Math.sqrt(swingProgress) * Math.PI * 2.0);
    const var6 = -0.4 * Math.sin(swingProgress * Math.PI);
    translate(var4 + bobX, var5 + bobY, var6);

    // 2. Base camera space offset
    translate(0.64000005, -0.6, -0.71999997);

    // 3. Equip progress (0 when fully equipped)
    translate(0.0, 0.0, 0.0);

    // 4. Rotate 45 deg around Y
    rotate(45.0, 0.0, 1.0, 0.0);

    // 5. Swing progress rotations
    const var7 = Math.sin(swingProgress * swingProgress * Math.PI);
    const var8 = Math.sin(Math.sqrt(swingProgress) * Math.PI);
    rotate(var8 * 70.0, 0.0, 1.0, 0.0);
    rotate(var7 * -20.0, 0.0, 0.0, 1.0);

    // 6. Viewmodel arm alignment transformations
    translate(-1.0, 3.6, 3.5);
    rotate(120.0, 0.0, 0.0, 1.0);
    rotate(200.0, 1.0, 0.0, 0.0);
    rotate(-135.0, 0.0, 1.0, 0.0);
    translate(5.6, 0.0, 0.0);

    // 7. ModelRenderer rotationPoint offset
    const sc = 0.0625;
    translate(-5.0 * sc, (isAlex ? 2.5 : 2.0) * sc, 0.0);

    this.fpArmMatrix.decompose(this.fpArmPos, this.fpArmQuat, this.fpArmScale);
  }

  private computeMinecraftItemTransform(
    swingProgress: number,
    bobX: number,
    bobY: number,
  ): void {
    this.fpItemMatrix.identity();

    const translate = (x: number, y: number, z: number) => {
      this.fpItemTrans.makeTranslation(x, y, z);
      this.fpItemMatrix.multiply(this.fpItemTrans);
    };

    const rotate = (deg: number, x: number, y: number, z: number) => {
      this.fpItemAxis.set(x, y, z).normalize();
      this.fpItemRot.makeRotationAxis(this.fpItemAxis, (deg * Math.PI) / 180);
      this.fpItemMatrix.multiply(this.fpItemRot);
    };

    const scale = (sx: number, sy: number, sz: number) => {
      this.fpItemScaleMat.makeScale(sx, sy, sz);
      this.fpItemMatrix.multiply(this.fpItemScaleMat);
    };

    // 1. Procedural swing translation (Minecraft ItemRenderer func_178105_d)
    const f = -0.4 * Math.sin(Math.sqrt(swingProgress) * Math.PI);
    const f1 = 0.2 * Math.sin(Math.sqrt(swingProgress) * Math.PI * 2.0);
    const f2 = -0.2 * Math.sin(swingProgress * Math.PI);
    translate(f + bobX, f1 + bobY, f2);

    // 2. Base camera space offset (Minecraft ItemRenderer transformFirstPersonItem)
    translate(0.56, -0.52, -0.72);
    rotate(45.0, 0.0, 1.0, 0.0);

    // 3. Attack swing rotations (Minecraft ItemRenderer transformFirstPersonItem)
    const fRotY = Math.sin(swingProgress * swingProgress * Math.PI);
    const fRotZ = Math.sin(Math.sqrt(swingProgress) * Math.PI);
    rotate(fRotY * -20.0, 0.0, 1.0, 0.0);
    rotate(fRotZ * -20.0, 0.0, 0.0, 1.0);
    rotate(fRotZ * -80.0, 1.0, 0.0, 0.0);
    scale(0.4, 0.4, 0.4);

    // 4. RenderItem.renderItemModelTransform preTransform scale
    // In official Minecraft 1.8.8 RenderItem.java (line 384), non-3D items receive scale(2.0, 2.0, 2.0)
    scale(2.0, 2.0, 2.0);

    // 5. Model transform (Minecraft ItemCameraTransforms applyTransform FIRST_PERSON)
    // From official Minecraft 1.8.8 model definition (diamond_axe.json, bone.json, torch.json, etc.):
    // translation: [0, 4, 2] * 0.0625 = [0.0, 0.25, 0.125]
    // rotation: [0, -135, 25] -> rotY(-135), rotX(0), rotZ(25)
    // scale: [1.7, 1.7, 1.7]
    translate(0.0, 0.25, 0.125);
    rotate(-135.0, 0.0, 1.0, 0.0);
    rotate(0.0, 1.0, 0.0, 0.0);
    rotate(25.0, 0.0, 0.0, 1.0);
    scale(1.7, 1.7, 1.7);

    // 6. RenderItem.renderItem model centering and normalization
    // In official RenderItem.java lines 130-145:
    // GlStateManager.scale(0.5F, 0.5F, 0.5F);
    // GlStateManager.translate(-0.5F, -0.5F, -0.5F);
    scale(0.5, 0.5, 0.5);
    translate(-0.5, -0.5, -0.5);

    this.fpItemMatrix.decompose(this.fpItemPos, this.fpItemQuat, this.fpItemScale);
  }

  private computeMinecraftSwordBlockTransform(
    swingProgress: number,
    bobX: number,
    bobY: number,
  ): void {
    this.fpItemMatrix.identity();

    const translate = (x: number, y: number, z: number) => {
      this.fpItemTrans.makeTranslation(x, y, z);
      this.fpItemMatrix.multiply(this.fpItemTrans);
    };

    const rotate = (deg: number, x: number, y: number, z: number) => {
      this.fpItemAxis.set(x, y, z).normalize();
      this.fpItemRot.makeRotationAxis(this.fpItemAxis, (deg * Math.PI) / 180);
      this.fpItemMatrix.multiply(this.fpItemRot);
    };

    const scale = (sx: number, sy: number, sz: number) => {
      this.fpItemScaleMat.makeScale(sx, sy, sz);
      this.fpItemMatrix.multiply(this.fpItemScaleMat);
    };

    // 1. Base transformFirstPersonItem(equip = 0, swingProgress)
    translate(0.56 + bobX, -0.52 + bobY, -0.72);
    rotate(45.0, 0.0, 1.0, 0.0);

    const fRotY = Math.sin(swingProgress * swingProgress * Math.PI);
    const fRotZ = Math.sin(Math.sqrt(swingProgress) * Math.PI);
    rotate(fRotY * -20.0, 0.0, 1.0, 0.0);
    rotate(fRotZ * -20.0, 0.0, 0.0, 1.0);
    rotate(fRotZ * -80.0, 1.0, 0.0, 0.0);
    scale(0.4, 0.4, 0.4);

    // 2. Sword block transform (Minecraft ItemRenderer func_178103_d)
    translate(-0.5, 0.2, 0.0);
    rotate(30.0, 0.0, 1.0, 0.0);
    rotate(-80.0, 1.0, 0.0, 0.0);
    rotate(60.0, 0.0, 1.0, 0.0);

    // 3. RenderItem.preTransform scale(2.0, 2.0, 2.0)
    scale(2.0, 2.0, 2.0);

    // 4. Model transform (ItemCameraTransforms FIRST_PERSON)
    translate(0.0, 0.25, 0.125);
    rotate(-135.0, 0.0, 1.0, 0.0);
    rotate(0.0, 1.0, 0.0, 0.0);
    rotate(25.0, 0.0, 0.0, 1.0);
    scale(1.7, 1.7, 1.7);

    // 5. Centering and normalization
    scale(0.5, 0.5, 0.5);
    translate(-0.5, -0.5, -0.5);

    this.fpItemMatrix.decompose(this.fpItemPos, this.fpItemQuat, this.fpItemScale);
  }

  private computeMinecraftBowTransform(
    bowCharge: number,
    bobX: number,
    bobY: number,
  ): void {
    this.fpItemMatrix.identity();

    const translate = (x: number, y: number, z: number) => {
      this.fpItemTrans.makeTranslation(x, y, z);
      this.fpItemMatrix.multiply(this.fpItemTrans);
    };

    const rotate = (deg: number, x: number, y: number, z: number) => {
      this.fpItemAxis.set(x, y, z).normalize();
      this.fpItemRot.makeRotationAxis(this.fpItemAxis, (deg * Math.PI) / 180);
      this.fpItemMatrix.multiply(this.fpItemRot);
    };

    const scale = (sx: number, sy: number, sz: number) => {
      this.fpItemScaleMat.makeScale(sx, sy, sz);
      this.fpItemMatrix.multiply(this.fpItemScaleMat);
    };

    // 1. Base first person item transform (ItemRenderer.transformFirstPersonItem with swingProgress = 0)
    translate(0.56 + bobX, -0.52 + bobY, -0.72);
    rotate(45.0, 0.0, 1.0, 0.0);
    scale(0.4, 0.4, 0.4);

    // 2. Bow aiming and draw transform (ItemRenderer.func_178098_a)
    rotate(-18.0, 0.0, 0.0, 1.0);
    rotate(-12.0, 0.0, 1.0, 0.0);
    rotate(-8.0, 1.0, 0.0, 0.0);
    translate(-0.9, 0.2, 0.0);

    const f = Math.min(1.0, bowCharge);
    const f1 = (f * f + f * 2.0) / 3.0; // Minecraft Java Edition bow pull formula

    if (f1 > 0.1) {
      const f2 = Math.sin((f * 20.0 - 0.1) * 1.3);
      const f3 = f1 - 0.1;
      const f4 = f2 * f3;
      translate(0.0, f4 * 0.01, 0.0); // Subtle bow shake / quiver when fully drawn
    }

    translate(0.0, 0.0, f1 * 0.1);
    scale(1.0, 1.0, 1.0 + f1 * 0.2);

    // 3. RenderItem.preTransform non-3D item scale(2.0, 2.0, 2.0)
    scale(2.0, 2.0, 2.0);

    // 4. Model transform (Minecraft ItemCameraTransforms FIRST_PERSON for bow)
    // translation: [0, 4, 2] * 0.0625 = [0.0, 0.25, 0.125]
    // rotation: [0, -135, 25] -> rotY(-135), rotX(0), rotZ(25)
    // scale: [1.7, 1.7, 1.7]
    translate(0.0, 0.25, 0.125);
    rotate(-135.0, 0.0, 1.0, 0.0);
    rotate(0.0, 1.0, 0.0, 0.0);
    rotate(25.0, 0.0, 0.0, 1.0);
    scale(1.7, 1.7, 1.7);

    // 5. RenderItem.renderItem model centering and normalization
    scale(0.5, 0.5, 0.5);
    translate(-0.5, -0.5, -0.5);

    this.fpItemMatrix.decompose(this.fpItemPos, this.fpItemQuat, this.fpItemScale);
  }

  private computeMinecraftThirdPersonItemTransform(
    heldId: number,
    isSword: boolean,
    isTool: boolean,
    isBow: boolean,
  ): void {
    this.tpItemMatrix.identity();

    const translate = (x: number, y: number, z: number) => {
      this.tpItemTrans.makeTranslation(x, y, z);
      this.tpItemMatrix.multiply(this.tpItemTrans);
    };

    const rotate = (deg: number, x: number, y: number, z: number) => {
      this.tpItemAxis.set(x, y, z).normalize();
      this.tpItemRot.makeRotationAxis(this.tpItemAxis, (deg * Math.PI) / 180);
      this.tpItemMatrix.multiply(this.tpItemRot);
    };

    const scale = (s: number) => {
      this.tpItemScaleMat.makeScale(s, s, s);
      this.tpItemMatrix.multiply(this.tpItemScaleMat);
    };

    if (isBow) {
      // Minecraft 1.8.8 Third-Person Bow (bow.json: rotation [5, 80, -45], translation [0.75, 0, 0.25], scale 1.0)
      translate(-0.0313, 0.1225, 0.0938);
      rotate(-80.0, 0, 1, 0);
      rotate(-5.0, 1, 0, 0);
      rotate(135.0, 0, 0, 1);
      scale(1.0);
      translate(-0.5, -0.5, -0.5);
    } else if (isSword || isTool || heldId === ARROW || heldId === STICK || heldId === BONE) {
      // Minecraft 1.8.8 Third-Person Handheld Tools & Weapons (handheld.json: rotation [0, 90, -35], translation [0, 1.25, -3.5], scale 0.85)
      // Grip rests perfectly inside palm; blade/head points straight forward in the sagittal plane with 0 outward tilt
      translate(0.0625, -0.0338, -0.375);
      rotate(-90.0, 0, 1, 0);
      rotate(0.0, 1, 0, 0);
      rotate(145.0, 0, 0, 1);
      scale(0.85);
      translate(-0.5, -0.5, -0.5);
    } else {
      // Minecraft 1.8.8 Third-Person Generated Items & Torches (food, ingots, diamonds, torches: rotation [-90, 0, 0], translation [0, 1, -3], scale 0.55)
      translate(0.0625, -0.0025, -0.3125);
      rotate(-180.0, 0, 1, 0);
      rotate(90.0, 1, 0, 0);
      rotate(0.0, 0, 0, 1);
      scale(0.55);
      translate(-0.5, -0.5, -0.5);
    }

    this.tpItemMatrix.decompose(this.tpItemPos, this.tpItemQuat, this.tpItemScale);
  }

  private getBowPullGeometry(stage: 0 | 1 | 2): THREE.BufferGeometry {
    let geo = this.bowPullGeometries.get(stage);
    if (!geo) {
      const grid = getBowPullingPixelGrid(stage);
      geo = createItemBufferGeometryFromGrid(grid);
      this.bowPullGeometries.set(stage, geo);
    }
    return geo;
  }

  private getBlockGeometry(id: number): THREE.BufferGeometry {
    let geo = this.blockGeometries.get(id);
    if (!geo) {
      geo = createBlockBufferGeometry(id, 1);
      this.blockGeometries.set(id, geo);
    }
    return geo;
  }

  private getItemGeometry(id: number): THREE.BufferGeometry {
    let geo = this.itemGeometries.get(id);
    if (!geo) {
      geo = createItemBufferGeometry(id);
      this.itemGeometries.set(id, geo);
    }
    return geo;
  }

  private buildRig(parentGroup: THREE.Group, skin: CharacterSkin): CharacterRig {
    const textures = skin === "steve" ? this.steveTextures : this.alexTextures;
    const materials: THREE.MeshBasicMaterial[] = [];

    const mat = (tex: THREE.CanvasTexture): THREE.MeshBasicMaterial => {
      const m = new THREE.MeshBasicMaterial({ map: tex, fog: true });
      materials.push(m);
      return m;
    };

    const isAlex = skin === "alex";
    const armWidth = isAlex ? 0.18 : 0.24; // Alex has slim 3-pixel arms

    // 1. Torso
    const torsoMats = [
      mat(textures.torsoSide),
      mat(textures.torsoSide),
      mat(textures.torsoTopBottom),
      mat(textures.torsoTopBottom),
      mat(textures.torsoBack),
      mat(textures.torsoFront),
    ];
    const torsoGroup = new THREE.Group();
    parentGroup.add(torsoGroup);
    const torsoMesh = new THREE.Mesh(this.boxGeometry, torsoMats);
    torsoMesh.position.set(0, 0.98, 0);
    torsoMesh.scale.set(0.44, 0.64, 0.24);
    torsoGroup.add(torsoMesh);

    // Alex 3D hair lock cascading on front chest (always created so material indices remain consistent)
    const lockMat = new THREE.MeshBasicMaterial({
      color: ALEX_HAIR_LOCK,
      fog: true,
    });
    materials.push(lockMat);
    const hairLockMesh = new THREE.Mesh(this.boxGeometry, lockMat);
    hairLockMesh.position.set(0.10, 1.14, -0.13);
    hairLockMesh.scale.set(0.10, 0.28, 0.04);
    hairLockMesh.visible = isAlex;
    torsoGroup.add(hairLockMesh);

    // 2. Head
    const headMats = [
      mat(textures.headSide),
      mat(textures.headSide),
      mat(textures.headTop),
      mat(textures.headBottom),
      mat(textures.headBack),
      mat(textures.headFront),
    ];
    const headPivot = new THREE.Group();
    headPivot.position.set(0, 1.30, 0);
    parentGroup.add(headPivot);
    const headMesh = new THREE.Mesh(this.boxGeometry, headMats);
    headMesh.position.set(0, 0.25, 0);
    headMesh.scale.set(0.50, 0.50, 0.50);
    headPivot.add(headMesh);

    // 3. Legs
    const legMats = [
      mat(textures.legSide),
      mat(textures.legSide),
      mat(textures.legTopBottom),
      mat(textures.legTopBottom),
      mat(textures.legSide),
      mat(textures.legFront),
    ];

    const leftLegPivot = new THREE.Group();
    leftLegPivot.position.set(-0.11, 0.66, 0);
    parentGroup.add(leftLegPivot);
    const leftLegMesh = new THREE.Mesh(this.boxGeometry, legMats);
    leftLegMesh.position.set(0, -0.33, 0);
    leftLegMesh.scale.set(0.20, 0.66, 0.20);
    leftLegPivot.add(leftLegMesh);

    const rightLegPivot = new THREE.Group();
    rightLegPivot.position.set(0.11, 0.66, 0);
    parentGroup.add(rightLegPivot);
    const rightLegMesh = new THREE.Mesh(this.boxGeometry, legMats);
    rightLegMesh.position.set(0, -0.33, 0);
    rightLegMesh.scale.set(0.20, 0.66, 0.20);
    rightLegPivot.add(rightLegMesh);

    // 4. Arms
    const armMats = [
      mat(textures.armSide),
      mat(textures.armSide),
      mat(textures.armTopBottom),
      mat(textures.armTopBottom),
      mat(textures.armSide),
      mat(textures.armFront),
    ];

    const leftArmPivot = new THREE.Group();
    leftArmPivot.position.set(-(0.22 + armWidth / 2), 1.25, 0);
    parentGroup.add(leftArmPivot);
    const leftArmMesh = new THREE.Mesh(this.boxGeometry, armMats);
    leftArmMesh.position.set(0, -0.32, 0);
    leftArmMesh.scale.set(armWidth, 0.64, 0.22);
    leftArmPivot.add(leftArmMesh);

    const rightArmPivot = new THREE.Group();
    rightArmPivot.position.set(0.22 + armWidth / 2, 1.25, 0);
    parentGroup.add(rightArmPivot);
    const rightArmMesh = new THREE.Mesh(this.boxGeometry, armMats);
    rightArmMesh.position.set(0, -0.32, 0);
    rightArmMesh.scale.set(armWidth, 0.64, 0.22);
    rightArmPivot.add(rightArmMesh);

    // Third-person held item slots (centered inside the character's right fist)
    const tpItemPivot = new THREE.Group();
    tpItemPivot.position.set(0, -0.56, 0.0);
    rightArmPivot.add(tpItemPivot);

    const tpItemMesh = new THREE.Mesh(this.boxGeometry, this.tpItemMat);
    tpItemMesh.scale.set(0.42, 0.42, 0.42);
    tpItemMesh.visible = false;
    tpItemPivot.add(tpItemMesh);

    const tpBlockGroup = new THREE.Group();
    const tpBlockMesh = new THREE.Mesh(this.getBlockGeometry(STONE), this.tpBlockMat);
    tpBlockMesh.position.set(0.0625, 0.0288, -0.1094);
    tpBlockMesh.rotation.set(-10 * (Math.PI / 180), 45 * (Math.PI / 180), -10 * (Math.PI / 180), "YXZ");
    tpBlockMesh.scale.set(0.24, 0.24, 0.24);
    tpBlockGroup.add(tpBlockMesh);
    tpItemPivot.add(tpBlockGroup);

    // 5. Armor meshes (outer layer on head, torso, arms, legs, boots)
    const defaultArmorSet = this.armorRegistry.get("iron");

    const helmetMesh = new THREE.Mesh(this.boxGeometry, defaultArmorSet.helmet);
    helmetMesh.position.set(0, 0.25, 0);
    helmetMesh.scale.set(0.56, 0.56, 0.56);
    helmetMesh.visible = false;
    headPivot.add(helmetMesh);

    const chestTorsoMesh = new THREE.Mesh(this.boxGeometry, defaultArmorSet.chestTorso);
    chestTorsoMesh.position.set(0, 0.98, 0);
    chestTorsoMesh.scale.set(0.48, 0.68, 0.28);
    chestTorsoMesh.visible = false;
    torsoGroup.add(chestTorsoMesh);

    const chestLeftSleeveMesh = new THREE.Mesh(this.boxGeometry, defaultArmorSet.chestSleeve);
    chestLeftSleeveMesh.position.set(0, -0.18, 0);
    chestLeftSleeveMesh.scale.set(armWidth + 0.04, 0.36, 0.26);
    chestLeftSleeveMesh.visible = false;
    leftArmPivot.add(chestLeftSleeveMesh);

    const chestRightSleeveMesh = new THREE.Mesh(this.boxGeometry, defaultArmorSet.chestSleeve);
    chestRightSleeveMesh.position.set(0, -0.18, 0);
    chestRightSleeveMesh.scale.set(armWidth + 0.04, 0.36, 0.26);
    chestRightSleeveMesh.visible = false;
    rightArmPivot.add(chestRightSleeveMesh);

    const leggingsPelvisMesh = new THREE.Mesh(this.boxGeometry, defaultArmorSet.leggingsPelvis);
    leggingsPelvisMesh.position.set(0, 0.72, 0);
    leggingsPelvisMesh.scale.set(0.46, 0.16, 0.26);
    leggingsPelvisMesh.visible = false;
    torsoGroup.add(leggingsPelvisMesh);

    const leggingsLeftLegMesh = new THREE.Mesh(this.boxGeometry, defaultArmorSet.leggingsLeg);
    leggingsLeftLegMesh.position.set(0, -0.20, 0);
    leggingsLeftLegMesh.scale.set(0.23, 0.40, 0.23);
    leggingsLeftLegMesh.visible = false;
    leftLegPivot.add(leggingsLeftLegMesh);

    const leggingsRightLegMesh = new THREE.Mesh(this.boxGeometry, defaultArmorSet.leggingsLeg);
    leggingsRightLegMesh.position.set(0, -0.20, 0);
    leggingsRightLegMesh.scale.set(0.23, 0.40, 0.23);
    leggingsRightLegMesh.visible = false;
    rightLegPivot.add(leggingsRightLegMesh);

    const bootsLeftMesh = new THREE.Mesh(this.boxGeometry, defaultArmorSet.boots);
    bootsLeftMesh.position.set(0, -0.50, 0);
    bootsLeftMesh.scale.set(0.24, 0.34, 0.24);
    bootsLeftMesh.visible = false;
    leftLegPivot.add(bootsLeftMesh);

    const bootsRightMesh = new THREE.Mesh(this.boxGeometry, defaultArmorSet.boots);
    bootsRightMesh.position.set(0, -0.50, 0);
    bootsRightMesh.scale.set(0.24, 0.34, 0.24);
    bootsRightMesh.visible = false;
    rightLegPivot.add(bootsRightMesh);

    const armorMeshes: ArmorRigMeshes = {
      helmet: helmetMesh,
      chestTorso: chestTorsoMesh,
      chestLeftSleeve: chestLeftSleeveMesh,
      chestRightSleeve: chestRightSleeveMesh,
      leggingsPelvis: leggingsPelvisMesh,
      leggingsLeftLeg: leggingsLeftLegMesh,
      leggingsRightLeg: leggingsRightLegMesh,
      bootsLeft: bootsLeftMesh,
      bootsRight: bootsRightMesh,
    };

    return {
      rootGroup: parentGroup,
      headPivot,
      torsoGroup,
      leftArmPivot,
      rightArmPivot,
      leftLegPivot,
      rightLegPivot,
      rightArmMesh,
      leftArmMesh,
      tpItemPivot,
      tpItemMesh,
      tpBlockGroup,
      tpBlockMesh,
      hairLockMesh,
      armorMeshes,
      materials,
      skin,
    };
  }

  private applySkinTextures(rig: CharacterRig, skin: CharacterSkin): void {
    if (rig.skin === skin) return;
    rig.skin = skin;
    const tex = skin === "steve" ? this.steveTextures : this.alexTextures;
    const isAlex = skin === "alex";
    const armW = isAlex ? 0.18 : 0.24;

    rig.leftArmMesh.scale.set(armW, 0.64, 0.22);
    rig.rightArmMesh.scale.set(armW, 0.64, 0.22);
    rig.armorMeshes.chestLeftSleeve.scale.x = armW + 0.04;
    rig.armorMeshes.chestRightSleeve.scale.x = armW + 0.04;
    rig.leftArmPivot.position.x = -(0.22 + armW / 2);
    rig.rightArmPivot.position.x = 0.22 + armW / 2;

    if (rig.hairLockMesh) {
      rig.hairLockMesh.visible = isAlex;
    }

    // Update material maps for rig
    // Torso materials: indices 0..5
    rig.materials[0].map = tex.torsoSide;
    rig.materials[1].map = tex.torsoSide;
    rig.materials[2].map = tex.torsoTopBottom;
    rig.materials[3].map = tex.torsoTopBottom;
    rig.materials[4].map = tex.torsoBack;
    rig.materials[5].map = tex.torsoFront;

    // Head materials: indices 7..12 (index 6 is Alex hair lock)
    rig.materials[7].map = tex.headSide;
    rig.materials[8].map = tex.headSide;
    rig.materials[9].map = tex.headTop;
    rig.materials[10].map = tex.headBottom;
    rig.materials[11].map = tex.headBack;
    rig.materials[12].map = tex.headFront;

    // Leg materials: indices 13..18
    rig.materials[13].map = tex.legSide;
    rig.materials[14].map = tex.legSide;
    rig.materials[15].map = tex.legTopBottom;
    rig.materials[16].map = tex.legTopBottom;
    rig.materials[17].map = tex.legSide;
    rig.materials[18].map = tex.legFront;

    // Arm materials: indices 19..24
    rig.materials[19].map = tex.armSide;
    rig.materials[20].map = tex.armSide;
    rig.materials[21].map = tex.armTopBottom;
    rig.materials[22].map = tex.armTopBottom;
    rig.materials[23].map = tex.armSide;
    rig.materials[24].map = tex.armFront;

    for (const m of rig.materials) m.needsUpdate = true;
  }

  sync(
    player: Player,
    light: number,
    dt: number,
    heldStack: ItemStack | null,
    isSwinging: boolean,
    bowCharge: number,
    armor?: readonly (ItemStack | null)[],
  ): void {
    const brightness = Math.max(0, light);
    const flash = player.hurtFlash > 0 ? 0.7 : 0;

    // Ensure rigs reflect player's skin and companion's alternate skin
    const playerSkin = player.skin;
    const companionSkin = playerSkin === "steve" ? "alex" : "steve";
    this.applySkinTextures(this.playerRig, playerSkin);
    this.applySkinTextures(this.companionRig, companionSkin);

    // Apply brightness and flash tint to player and armor materials
    for (const mat of this.playerRig.materials) {
      mat.color.setScalar(brightness);
      if (flash > 0) mat.color.lerp(FLASH, flash);
    }
    for (const mat of this.companionRig.materials) {
      mat.color.setScalar(brightness);
    }
    for (const { mat, baseColor } of this.fpMaterials) {
      mat.color.copy(baseColor).multiplyScalar(brightness);
      if (flash > 0) mat.color.lerp(FLASH, flash);
    }
    for (const mat of this.armorMaterials) {
      mat.color.setScalar(brightness);
      if (flash > 0) mat.color.lerp(FLASH, flash);
    }

    this.updateRigArmor(this.playerRig, armor);
    this.updateRigArmor(this.companionRig, undefined);

    // Walking animation for player
    const speed = Math.hypot(player.velocity.x, player.velocity.z);
    const isMoving = speed > 0.1 && (player.onGround || player.inWater);
    if (isMoving) {
      this.walkPhase += dt * Math.min(speed, 5.5) * 3.8;
    } else {
      this.walkPhase *= Math.max(0, 1 - dt * 8);
    }

    // Authentic Minecraft Java Edition arm swing animation:
    // Base swing duration is exactly 6 game ticks (6 / 20 = 0.300s, frequency = 20 / 6 ≈ 3.333 Hz)
    const SWING_RATE = 20.0 / 6.0;
    if (isSwinging || this.swingProgress > 0) {
      this.swingProgress += dt * SWING_RATE;
      if (this.swingProgress >= 1) {
        this.swingProgress = isSwinging ? this.swingProgress % 1 : 0;
      }
    }

    const heldId = heldStack?.id ?? 0;
    const isBow = heldId === BOW;
    const isSwordItem = isSword(heldId);
    const isToolItem = isTool(heldId);
    const isTorch = heldId === TORCH;
    const isBlock = isBlockItem(heldId) && !isTorch && (BLOCKS[heldId]?.top ?? -1) >= 0;
    const isItem = (heldId > 0 && !isBlock) || isTorch;

    if (isBlock) {
      const blockGeo = this.getBlockGeometry(heldId);
      this.fpBlockMesh.geometry = blockGeo;
      this.playerRig.tpBlockMesh.geometry = blockGeo;
      this.companionRig.tpBlockMesh.geometry = blockGeo;
    }

    if (isItem) {
      if (isBow && bowCharge > 0) {
        let stage: 0 | 1 | 2 = 0;
        if (bowCharge >= 0.9) stage = 2;
        else if (bowCharge >= 0.65) stage = 1;
        const bowGeo = this.getBowPullGeometry(stage);
        this.fpItemMesh.geometry = bowGeo;
        this.playerRig.tpItemMesh.geometry = bowGeo;
        this.companionRig.tpItemMesh.geometry = bowGeo;
      } else {
        const itemGeo = this.getItemGeometry(heldId);
        this.fpItemMesh.geometry = itemGeo;
        this.playerRig.tpItemMesh.geometry = itemGeo;
        this.companionRig.tpItemMesh.geometry = itemGeo;
      }
    }

    this.playerRig.tpItemMesh.visible = isItem;
    this.playerRig.tpBlockGroup.visible = isBlock;
    this.companionRig.tpItemMesh.visible = false;
    this.companionRig.tpBlockGroup.visible = false;

    this.fpBlockMat.color.setScalar(brightness);
    if (flash > 0) this.fpBlockMat.color.lerp(FLASH, flash);
    this.tpBlockMat.color.setScalar(brightness);

    this.fpItemMat.color.setScalar(brightness);
    if (flash > 0) this.fpItemMat.color.lerp(FLASH, flash);
    this.tpItemMat.color.setScalar(brightness);

    // --- 1. First-Person Viewmodel ---
    if (player.cameraMode === "first-person") {
      this.playerThirdPersonGroup.visible = false;
      this.firstPersonGroup.visible = !player.isDead;

      const showArm = heldId === 0;
      this.fpArmMesh.visible = showArm;
      this.fpBlockGroup.visible = isBlock;
      this.fpItemMesh.visible = isItem;

      // Update 1st-person arm texture and geometry for current skin
      if (showArm) {
        const isAlex = playerSkin === "alex";
        this.fpArmMesh.geometry = isAlex ? this.alexFpArmGeo : this.steveFpArmGeo;
        const pTex = isAlex ? this.alexTextures : this.steveTextures;
        this.fpArmMats[0].map = pTex.fpArm;
        this.fpArmMats[1].map = pTex.fpArm;
        this.fpArmMats[2].map = pTex.armHandBottom;
        this.fpArmMats[3].map = pTex.armHandBottom;
        this.fpArmMats[4].map = pTex.fpArm;
        this.fpArmMats[5].map = pTex.fpArm;
        for (const m of this.fpArmMats) m.needsUpdate = true;
      }

      const bobX = this.viewBobbing ? Math.cos(this.walkPhase * 0.5) * 0.012 : 0;
      const bobY = this.viewBobbing ? -Math.abs(Math.sin(this.walkPhase)) * 0.016 : 0;
      const swing = Math.sin(this.swingProgress * Math.PI);

      if (isBow && bowCharge > 0) {
        // Authentic Minecraft Java Edition first-person bow drawing matrix (ItemRenderer.func_178098_a)
        this.firstPersonGroup.position.set(0, 0, 0);
        this.firstPersonGroup.rotation.set(0, 0, 0);

        this.computeMinecraftBowTransform(bowCharge, bobX, bobY);
        this.fpItemMesh.position.copy(this.fpItemPos);
        this.fpItemMesh.quaternion.copy(this.fpItemQuat);
        this.fpItemMesh.scale.copy(this.fpItemScale);
      } else if (isSwordItem && player.blocking) {
        // Authentic Minecraft Java Edition sword blocking matrix (ItemRenderer.func_178103_d)
        this.firstPersonGroup.position.set(0, 0, 0);
        this.firstPersonGroup.rotation.set(0, 0, 0);

        this.computeMinecraftSwordBlockTransform(this.swingProgress, bobX, bobY);
        this.fpItemMesh.position.copy(this.fpItemPos);
        this.fpItemMesh.quaternion.copy(this.fpItemQuat);
        this.fpItemMesh.scale.copy(this.fpItemScale);
      } else if (isBlock) {
        // Authentic Minecraft held block view:
        // Lower-right isometric perspective with forward dipping punch swing
        const baseX = 0.36 + bobX;
        const baseY = -0.28 + bobY;
        const baseZ = -0.48;

        const swingPitch = -swing * 0.65;
        const swingYaw = swing * 0.30;
        const swingRoll = -swing * 0.20;
        const swingDipY = -swing * 0.06;
        const swingPushZ = -swing * 0.08;

        this.firstPersonGroup.position.set(
          baseX,
          baseY + swingDipY,
          baseZ + swingPushZ,
        );
        this.firstPersonGroup.rotation.set(
          -0.45 + swingPitch,
          0.65 + swingYaw,
          -0.15 + swingRoll,
        );
      } else if (isItem) {
        // Authentic Minecraft Java Edition first-person item renderer matrix
        this.firstPersonGroup.position.set(0, 0, 0);
        this.firstPersonGroup.rotation.set(0, 0, 0);

        this.computeMinecraftItemTransform(this.swingProgress, bobX, bobY);
        this.fpItemMesh.position.copy(this.fpItemPos);
        this.fpItemMesh.quaternion.copy(this.fpItemQuat);
        this.fpItemMesh.scale.copy(this.fpItemScale);
      } else {
        // Authentic Minecraft empty-hand arm:
        // Direct Java Edition ItemRenderer matrix simulation with 45° axial rotation,
        // diamond hand cap, vertical left edge, and directional facet shading matching clipboard18.png.
        this.firstPersonGroup.position.set(0, 0, 0);
        this.firstPersonGroup.rotation.set(0, 0, 0);

        const isAlex = playerSkin === "alex";
        this.computeMinecraftArmTransform(this.swingProgress, bobX, bobY, isAlex);
        this.fpArmMesh.position.copy(this.fpArmPos);
        this.fpArmMesh.quaternion.copy(this.fpArmQuat);
        this.fpArmMesh.scale.copy(this.fpArmScale);
      }
    } else {
      // Third-person mode
      this.firstPersonGroup.visible = false;
      this.playerThirdPersonGroup.visible = !player.isDead;

      const sneakOffset = player.sneaking ? 0.18 : 0;
      this.playerThirdPersonGroup.position.set(
        player.position.x,
        player.position.y - sneakOffset,
        player.position.z,
      );
      this.playerThirdPersonGroup.rotation.y = player.yaw;

      this.playerRig.headPivot.rotation.x = player.pitch;

      if (player.sneaking) {
        this.playerRig.torsoGroup.rotation.x = -0.26;
        this.playerRig.headPivot.position.set(0, 1.16, -0.12);
      } else {
        this.playerRig.torsoGroup.rotation.x = 0;
        this.playerRig.headPivot.position.set(0, 1.30, 0);
      }

      const legSwing = Math.sin(this.walkPhase) * 0.62;
      this.playerRig.leftLegPivot.rotation.x = -legSwing;
      this.playerRig.rightLegPivot.rotation.x = legSwing;

      // Authentic Minecraft third-person held item transforms
      this.computeMinecraftThirdPersonItemTransform(
        heldId,
        isSwordItem,
        isToolItem,
        isBow,
      );
      this.playerRig.tpItemMesh.position.copy(this.tpItemPos);
      this.playerRig.tpItemMesh.quaternion.copy(this.tpItemQuat);
      this.playerRig.tpItemMesh.scale.copy(this.tpItemScale);

      const swingAngle = Math.sin(this.swingProgress * Math.PI) * 0.8;
      if (bowCharge > 0) {
        this.playerRig.rightArmPivot.rotation.set(Math.PI / 2 + player.pitch, 0.25, 0);
        this.playerRig.leftArmPivot.rotation.set(Math.PI / 2 + player.pitch, -0.35, 0);
      } else if (isSwordItem && player.blocking) {
        // Authentic Minecraft third-person sword blocking pose (ModelBiped.java case 3: rotateAngleX = -PI/10, rotateAngleY = -30 deg inward)
        this.playerRig.rightArmPivot.rotation.set(0.314, 0.524, 0);
        this.playerRig.leftArmPivot.rotation.set(legSwing * 0.75, 0, 0);
      } else if (isSwinging || this.swingProgress > 0) {
        this.playerRig.rightArmPivot.rotation.set(0.5 + swingAngle, -0.15, 0);
        this.playerRig.leftArmPivot.rotation.set(legSwing * 0.5, 0, 0);
      } else if (heldId > 0) {
        // Authentic Minecraft third-person held item arm pose (ModelBiped.java: rightArmPivot.rotation.x = -legSwing * 0.5 + PI/10)
        this.playerRig.rightArmPivot.rotation.set(-legSwing * 0.5 + 0.314, 0, 0);
        this.playerRig.leftArmPivot.rotation.set(legSwing * 0.75, 0, 0);
      } else {
        this.playerRig.rightArmPivot.rotation.set(-legSwing * 0.75, 0, 0);
        this.playerRig.leftArmPivot.rotation.set(legSwing * 0.75, 0, 0);
      }
    }

    // --- 2. In-World Companion Character ---
    this.updateCompanion(player, dt);
  }

  private updateCompanion(player: Player, dt: number): void {
    if (!this.companionInitialized) {
      // Spawn companion 1.5 blocks behind and slightly right of player
      const sin = Math.sin(player.yaw);
      const cos = Math.cos(player.yaw);
      this.companionPos.set(
        player.position.x + cos * 1.2 + sin * 1.5,
        player.position.y,
        player.position.z - sin * 1.2 + cos * 1.5,
      );
      this.companionYaw = player.yaw;
      this.companionInitialized = true;
    }

    const dx = player.position.x - this.companionPos.x;
    const dz = player.position.z - this.companionPos.z;
    const distSq = dx * dx + dz * dz;

    if (distSq > 30 * 30) {
      // Teleport if player traveled very far
      this.companionPos.set(
        player.position.x + 1.2,
        player.position.y,
        player.position.z + 1.2,
      );
    } else if (distSq > 2.2 * 2.2) {
      // Walk towards player
      const angle = Math.atan2(-dx, -dz);
      this.companionYaw = angle;
      const walkSpeed = Math.min(4.5, Math.sqrt(distSq) * 1.5);
      this.companionPos.x += -Math.sin(angle) * walkSpeed * dt;
      this.companionPos.z += -Math.cos(angle) * walkSpeed * dt;
      this.companionPos.y = THREE.MathUtils.lerp(this.companionPos.y, player.position.y, dt * 10);
      this.companionWalkPhase += dt * walkSpeed * 4.0;
    } else {
      // Close to player: stop and face player's view direction
      this.companionYaw = THREE.MathUtils.lerp(this.companionYaw, player.yaw, dt * 4);
      this.companionWalkPhase *= Math.max(0, 1 - dt * 6);
    }

    this.companionGroup.position.copy(this.companionPos);
    this.companionGroup.rotation.y = this.companionYaw;

    const compLegSwing = Math.sin(this.companionWalkPhase) * 0.55;
    this.companionRig.leftLegPivot.rotation.x = -compLegSwing;
    this.companionRig.rightLegPivot.rotation.x = compLegSwing;
    this.companionRig.leftArmPivot.rotation.set(compLegSwing * 0.7, 0, 0);
    this.companionRig.rightArmPivot.rotation.set(-compLegSwing * 0.7, 0, 0);
    this.companionRig.headPivot.rotation.x = Math.sin(this.companionWalkPhase * 0.3) * 0.05;

    this.companionGroup.visible = !player.isDead;
  }

  private updateRigArmor(rig: CharacterRig, armor?: readonly (ItemStack | null)[]): void {
    const helmetStack = armor?.[ARMOR_HELMET] ?? null;
    const chestStack = armor?.[ARMOR_CHESTPLATE] ?? null;
    const leggingsStack = armor?.[ARMOR_LEGGINGS] ?? null;
    const bootsStack = armor?.[ARMOR_BOOTS] ?? null;

    const hInfo = helmetStack ? armorInfo(helmetStack.id) : null;
    if (hInfo) {
      const set = this.armorRegistry.get(hInfo.tier);
      rig.armorMeshes.helmet.visible = true;
      rig.armorMeshes.helmet.material = set.helmet;
    } else {
      rig.armorMeshes.helmet.visible = false;
    }

    const cInfo = chestStack ? armorInfo(chestStack.id) : null;
    if (cInfo) {
      const set = this.armorRegistry.get(cInfo.tier);
      rig.armorMeshes.chestTorso.visible = true;
      rig.armorMeshes.chestLeftSleeve.visible = true;
      rig.armorMeshes.chestRightSleeve.visible = true;
      rig.armorMeshes.chestTorso.material = set.chestTorso;
      rig.armorMeshes.chestLeftSleeve.material = set.chestSleeve;
      rig.armorMeshes.chestRightSleeve.material = set.chestSleeve;
    } else {
      rig.armorMeshes.chestTorso.visible = false;
      rig.armorMeshes.chestLeftSleeve.visible = false;
      rig.armorMeshes.chestRightSleeve.visible = false;
    }

    const lInfo = leggingsStack ? armorInfo(leggingsStack.id) : null;
    if (lInfo) {
      const set = this.armorRegistry.get(lInfo.tier);
      rig.armorMeshes.leggingsPelvis.visible = true;
      rig.armorMeshes.leggingsLeftLeg.visible = true;
      rig.armorMeshes.leggingsRightLeg.visible = true;
      rig.armorMeshes.leggingsPelvis.material = set.leggingsPelvis;
      rig.armorMeshes.leggingsLeftLeg.material = set.leggingsLeg;
      rig.armorMeshes.leggingsRightLeg.material = set.leggingsLeg;
    } else {
      rig.armorMeshes.leggingsPelvis.visible = false;
      rig.armorMeshes.leggingsLeftLeg.visible = false;
      rig.armorMeshes.leggingsRightLeg.visible = false;
    }

    const bInfo = bootsStack ? armorInfo(bootsStack.id) : null;
    if (bInfo) {
      const set = this.armorRegistry.get(bInfo.tier);
      rig.armorMeshes.bootsLeft.visible = true;
      rig.armorMeshes.bootsRight.visible = true;
      rig.armorMeshes.bootsLeft.material = set.boots;
      rig.armorMeshes.bootsRight.material = set.boots;
    } else {
      rig.armorMeshes.bootsLeft.visible = false;
      rig.armorMeshes.bootsRight.visible = false;
    }
  }

  dispose(): void {
    this.playerThirdPersonGroup.removeFromParent();
    this.companionGroup.removeFromParent();
    this.firstPersonGroup.removeFromParent();
    this.boxGeometry.dispose();
    this.steveFpArmGeo.dispose();
    this.alexFpArmGeo.dispose();
    for (const geo of this.blockGeometries.values()) geo.dispose();
    this.blockGeometries.clear();
    for (const geo of this.itemGeometries.values()) geo.dispose();
    this.itemGeometries.clear();
    for (const geo of this.bowPullGeometries.values()) geo.dispose();
    this.bowPullGeometries.clear();
    this.fpBlockMat.dispose();
    this.tpBlockMat.dispose();
    this.fpItemMat.dispose();
    this.tpItemMat.dispose();
    this.armorRegistry.dispose();
    for (const m of this.playerRig.materials) m.dispose();
    for (const m of this.companionRig.materials) m.dispose();
    for (const { mat } of this.fpMaterials) mat.dispose();
  }
}
